import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

// Configurar las fuentes por defecto
pdfMake.vfs = pdfFonts.vfs;

export default class PdfService {
  private logoUrl: string;

  constructor() {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const logoPath = require("@/assets/images/logo.png");
    this.logoUrl = logoPath; // Cambia esta ruta al logo correcto
  }

  /**
   * Genera un PDF para el cronograma de pagos de un crédito.
   * @param credit Detalles del crédito.
   * @param userInfo Información del usuario.
   */
  public async generateCreditPaymentSchedulePdf(
    credit: any,
    userInfo: any
  ): Promise<void> {
    const docDefinition = this.createCreditPaymentScheduleDefinition(
      credit,
      userInfo
    );

    try {
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);

      const blob: Blob = await new Promise((resolve, reject) => {
        pdfDocGenerator.getBlob((blob) => resolve(blob));
      });

      const timestamp = new Date().getTime();
      const fileName = `${credit.idcredito}_${timestamp}.pdf`;

      this.downloadPdf(blob, fileName);
    } catch (error) {
      console.error("Error al generar el PDF:", error);
      throw new Error("Hubo un error al generar el PDF.");
    }
  }

  /**
   * Crea la definición del documento para el cronograma de pagos.
   * @param credit Detalles del crédito.
   * @param userInfo Información del usuario.
   * @returns Definición del documento.
   */
  private createCreditPaymentScheduleDefinition(
    credit: any,
    userInfo: any
  ): any {
    const tableHeaders = [
      "Mes",
      "Vencimiento",
      "Interés",
      "Capital",
      "Aporte",
      "Seguro",
      "Cuota",
      "Saldo capital",
      "Cancelado",
    ];

    const tableRows = credit.cronogramas.map((item: any) => {
      const coin = this.formatCoin(credit.moneda);
      return [
        item["numcuota"],
        this.formatDate(item["fechacuota"]),
        `${coin}${this.formatFloat(item["interes"])}`,
        `${coin}${this.formatFloat(item["capital"])}`,
        `${coin}${this.formatFloat(item["aporte"])}`,
        `${coin}${this.formatFloat(item["seguro"])}`,
        `${coin}${this.formatFloat(item["cuota"])}`,
        `${coin}${this.formatFloat(item["saldo"])}`,
        item["es_cancelado"] == "1" ? "Sí" : "No",
      ];
    });

    return {
      content: [
        {
          image: this.logoUrl,
          width: 150,
          alignment: "center",
        },
        {
          text:
            "Cronograma de pagos - " +
            credit.idcredito +
            " - " +
            (userInfo.esnatural == 1
              ? this.capitalize(`${userInfo.nom} ${userInfo.ap} ${userInfo.am}`)
              : this.capitalize(userInfo.razonsocial)),
          style: "header",
          alignment: "center",
        },
        {
          table: {
            headerRows: 1,
            widths: ["auto", "*", "*", "*", "*", "*", "*", "*", "*"],
            body: [tableHeaders, ...tableRows],
          },
          layout: "noBorders",
        },
      ],
      styles: {
        header: {
          fontSize: 14,
          bold: true,
          margin: [0, 10, 0, 10],
        },
      },
    };
  }
  /**
   * Genera un PDF para el cronograma de ahorros de un usuario.
   * @param saving Detalles del ahorro.
   * @param userInfo Información del usuario.
   */
  public async exportSavingSchedule(saving: any, userInfo: any): Promise<void> {
    const docDefinition = this.createSavingScheduleDefinition(saving, userInfo);

    try {
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);

      const blob: Blob = await new Promise((resolve, reject) => {
        pdfDocGenerator.getBlob((blob) => resolve(blob));
      });

      const timestamp = new Date().getTime();
      const fileName = `Ahorro_${saving.id}_${timestamp}.pdf`;

      this.downloadPdf(blob, fileName);
    } catch (error) {
      console.error("Error al generar el PDF de ahorro:", error);
      throw new Error("Hubo un error al generar el PDF.");
    }
  }

  /**
   * Crea la definición del documento para el cronograma de ahorros.
   * @param saving Detalles del ahorro.
   * @param userInfo Información del usuario.
   * @returns Definición del documento.
   */
  private createSavingScheduleDefinition(saving: any, userInfo: any): any {
    const tableHeaders = ["Periodo", "Fecha", "Interés", "Capital", "Saldo"];

    const tableRows = saving.cronogramas.map((item: any) => {
      const coin = this.formatCoin(saving.moneda);
      return [
        item["periodo"],
        this.formatDate(item["fecha"]),
        `${coin}${this.formatFloat(item["interes"])}`,
        `${coin}${this.formatFloat(item["capital"])}`,
        `${coin}${this.formatFloat(item["saldo"])}`,
      ];
    });

    return {
      content: [
        {
          image: this.logoUrl,
          width: 100,
          alignment: "center",
        },
        {
          text:
            "Cronograma de Ahorros - " +
            saving.id +
            " - " +
            (userInfo.esnatural == 1
              ? this.capitalize(`${userInfo.nom} ${userInfo.ap} ${userInfo.am}`)
              : this.capitalize(userInfo.razonsocial)),
          style: "header",
          alignment: "center",
        },
        {
          table: {
            headerRows: 1,
            widths: ["auto", "*", "*", "*", "*"],
            body: [tableHeaders, ...tableRows],
          },
          layout: "noBorders",
        },
      ],
      styles: {
        header: {
          fontSize: 14,
          bold: true,
          margin: [0, 10, 0, 10],
        },
      },
    };
  }
  /**
   * Genera un PDF para el estado de cuenta de un ahorro.
   * @param savingStatement Detalles del estado de cuenta del ahorro.
   * @param saving Detalles del ahorro.
   * @param userInfo Información del usuario.
   */
  public async exportSavingStatement(
    savingStatement: any,
    saving: any,
    userInfo: any
  ): Promise<void> {
    const docDefinition = this.createSavingStatementDefinition(
      savingStatement,
      saving,
      userInfo
    );

    try {
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);

      const blob: Blob = await new Promise((resolve, reject) => {
        pdfDocGenerator.getBlob((blob) => resolve(blob));
      });

      const timestamp = new Date().getTime();
      const fileName = `EstadoCuenta_Ahorro_${saving.id}_${timestamp}.pdf`;

      this.downloadPdf(blob, fileName);
    } catch (error) {
      console.error("Error al generar el PDF del estado de cuenta:", error);
      throw new Error("Hubo un error al generar el PDF del estado de cuenta.");
    }
  }

  /**
   * Crea la definición del documento para el estado de cuenta de ahorros.
   * @param savingStatement Detalles del estado de cuenta.
   * @param saving Detalles del ahorro.
   * @param userInfo Información del usuario.
   * @returns Definición del documento.
   */
  private createSavingStatementDefinition(
    savingStatement: any[],
    saving: any,
    userInfo: any
  ): any {
    const tableHeaders = ["Fecha", "Hora", "Concepto", "Monto", "Saldo"];

    const tableRows = savingStatement.map((item: any) => {
      const coin = this.formatCoin(saving.moneda);
      return [
        this.formatDate(item["fecharegistro"]),
        item["hora"],
        item["concepto"],
        `${coin}${this.formatFloat(item["total"])}`,
        `${coin}${this.formatFloat(item["saldo"])}`,
      ];
    });

    return {
      content: [
        {
          image: this.logoUrl,
          width: 150,
          alignment: "center",
        },
        {
          text:
            "Estado de Cuenta de Ahorros - " +
            saving.id +
            " - " +
            (userInfo.esnatural == 1
              ? this.capitalize(`${userInfo.nom} ${userInfo.ap} ${userInfo.am}`)
              : this.capitalize(userInfo.razonsocial)),
          style: "header",
          alignment: "center",
        },
        {
          table: {
            headerRows: 1,
            widths: ["*", "*", "*", "*", "*"],
            body: [tableHeaders, ...tableRows],
          },
          layout: "noBorders",
        },
      ],
      styles: {
        header: {
          fontSize: 14,
          bold: true,
          margin: [0, 10, 0, 10],
        },
      },
    };
  }
  /**
   * Descarga un archivo PDF en el navegador.
   * @param blob Blob del PDF.
   * @param fileName Nombre del archivo.
   */
  private downloadPdf(blob: Blob, fileName: string): void {
    const pdfUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /**
   * Formatea una moneda.
   * @param coin Código de la moneda.
   * @returns Símbolo de la moneda.
   */
  private formatCoin(coin: number): string {
    // Implementa tu lógica de moneda aquí
    return `$`; // Ejemplo
  }

  /**
   * Formatea un número flotante.
   * @param number Número a formatear.
   * @returns Número formateado.
   */
  private formatFloat(number: number): string {
    // Implementa tu lógica de formato aquí
    return number.toFixed(2); // Ejemplo
  }

  /**
   * Formatea una fecha.
   * @param date Fecha en formato ISO.
   * @returns Fecha formateada.
   */
  private formatDate(date: string): string {
    // Implementa tu lógica de formato aquí
    return new Date(date).toLocaleDateString(); // Ejemplo
  }

  /**
   * Capitaliza una cadena de texto.
   * @param text Texto a capitalizar.
   * @returns Texto capitalizado.
   */
  private capitalize(text: string): string {
    return text
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }
}
